import type { PairMessage } from "@cronika/proto/common/trading_pb";
import { Search } from "@cronika/assets/icons";
import { useTranslation } from "@cronika/locales";
import { cn, Input } from "@cronika/ui";
import { type FC, useState } from "react";
import { useSymbolIcon } from "@/shared/lib/hooks/useSymbolIcon";
import type { TAddCategory, TCategory } from "../types";
import { PairList } from "./PairList";

type TAddPairDropdownProps = {
  pairs: PairMessage[];
  categories: TCategory[];
  selectedCategory: TAddCategory;
  onCategoryChange: (category: TAddCategory) => void;
  activePair: PairMessage | null;
  selectedPairs: PairMessage[];
  favoritePairs?: PairMessage[];
  onToggleFavorite?: (pair: PairMessage) => void;
  selectPairAsActive?: (pairId: string) => void;
  onPairSelect: (pair: PairMessage) => void;
  onRemovePair: (pairId: string) => void;
};

const ActivePairHeader: FC<{ pair: PairMessage | null }> = ({ pair }) => {
  const icon = useSymbolIcon(pair?.type, pair?.baseAsset, pair?.quoteAsset);

  if (!pair) return null;

  return (
    <div className="flex items-center gap-2 px-4 pt-4 pb-3">
      {icon ? (
        <img src={icon} alt={pair.symbol} className="h-7 w-7 object-contain" />
      ) : (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-default-200 text-[9px] font-medium">
          {pair.baseAsset.slice(0, 2)}
        </span>
      )}
      <span className="text-sm font-semibold text-white">{pair.symbol}</span>
    </div>
  );
};

export const AddPairDropdown: FC<TAddPairDropdownProps> = ({
  pairs,
  categories,
  selectedCategory,
  onCategoryChange,
  activePair,
  selectedPairs,
  favoritePairs = [],
  onToggleFavorite,
  selectPairAsActive,
  onPairSelect,
  onRemovePair,
}) => {
  const { t } = useTranslation("pairs-control");
  const [searchQuery, setSearchQuery] = useState("");

  return (
    <div className="flex h-[65vh]">
      {/* Left sidebar — categories */}
      <div className="flex w-[174px] shrink-0 flex-col border-r border-white/5 pt-[12px] pb-2">
        {categories.map((cat) => {
          const isActive = selectedCategory === cat.key;
          return (
            <button
              key={cat.key}
              type="button"
              onClick={() => onCategoryChange(cat.key)}
              className={cn(
                "flex items-center gap-2.5 px-5 py-2.5 text-[14px] font-medium transition-colors text-left",
                isActive ? "text-[#F4A425]" : "text-[#8C8C8C] hover:text-white",
              )}
            >
              {typeof cat.icon === "string" ? (
                <span className="text-base">{cat.icon}</span>
              ) : (
                <cat.icon className={cn("h-5 w-5 shrink-0", isActive ? "text-[#F4A425]" : "text-[#8C8C8C]")} />
              )}
              <span className="truncate">{cat.label}</span>
            </button>
          );
        })}
      </div>

      {/* Right panel — search + pair list */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <ActivePairHeader pair={activePair} />

        <div className="px-3.5 pb-4">
          <Input
            variant="grey-15"
            inputSize="md"
            placeholder={t("pairs-control.search-placeholder")}
            startContent={<Search />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <PairList
          pairs={pairs}
          selectedCategory={selectedCategory}
          activePair={activePair}
          selectedPairs={selectedPairs}
          favoritePairs={favoritePairs}
          onToggleFavorite={onToggleFavorite}
          selectPairAsActive={selectPairAsActive}
          onPairSelect={onPairSelect}
          onRemovePair={onRemovePair}
          searchQuery={searchQuery}
        />
      </div>
    </div>
  );
};
